import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import Switch from "react-switch";
import "./rules.css";
import { fetchThemeData } from "../../themeSlice";
import { processQuestions } from "../../questionSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { useNavigate } from "react-router-dom";
import { updateThemeData } from "../../functions/updateThemeData";

const defaultThankyouMessage = {
  energy: {
    range_0_30: "Energy needs attention.\nConsider pacing your day.",
    range_30_60: "Solid energy balance.",
    range_60_plus: "Excellent energy management!",
  },
  time: {
    range_0_30: "Time felt tight.\nTry blocking focus time.",
    range_30_60: "You kept time in check.",
    range_60_plus: "Outstanding time stewardship!",
  },
  heart: {
    range_0_30: "Health took a hit.\nPrioritize recovery.",
    range_30_60: "Health is on track.",
    range_60_plus: "Great job protecting your health!",
  },
};

function migrateThankyouMessage(raw) {
  if (!raw || typeof raw !== "object") return defaultThankyouMessage;
  if (Array.isArray(raw.energy)) {
    const e = raw.energy[0]?.message || "";
    const t = raw.time?.[0]?.message || e;
    const h = raw.heart?.[0]?.message || e;
    return {
      energy: { range_0_30: e, range_30_60: e, range_60_plus: e },
      time: { range_0_30: t, range_30_60: t, range_60_plus: t },
      heart: { range_0_30: h, range_30_60: h, range_60_plus: h },
    };
  }
  return {
    energy: { ...defaultThankyouMessage.energy, ...raw.energy },
    time: { ...defaultThankyouMessage.time, ...raw.time },
    heart: { ...defaultThankyouMessage.heart, ...raw.heart },
  };
}

const THANK_YOU_OUTCOME_MAX_CHARS = 200;

function clampThankyouOutcomeMessage(msg, maxLen) {
  if (!msg || typeof msg !== "object") return msg;
  const cats = ["energy", "time", "heart"];
  const rangeKeys = ["range_0_30", "range_30_60", "range_60_plus"];
  const out = { ...msg };
  for (const cat of cats) {
    if (typeof out[cat] !== "object" || out[cat] == null || Array.isArray(out[cat])) continue;
    const next = { ...out[cat] };
    for (const k of rangeKeys) {
      if (typeof next[k] === "string" && next[k].length > maxLen) next[k] = next[k].slice(0, maxLen);
    }
    out[cat] = next;
  }
  return out;
}

const defaultColors = {
  energy_color: "#e3e32f",
  time_color: "#6fa7e3",
  heart_color: "#d8381f",
  heading_text_color: "#4fd1c0",
  question_color: "#b2a7a7",
  button_text_color: "#cdcd9f",
  option_bg_color: "#e8e8e8",
  main_color_box: "#f4f4f4",
  do_u_know_bg_color: "#308f63",
  do_u_know_bg_text_color: "#000000",
  box_text_color: "#000000",
  box_bg_color: "#ffffff",
};

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { availableThemes, currentTheme, status, data } = useSelector(
    (state) => state.theme
  );

  const [customTextThankYouPage, setCustomTextThankYouPage] = useState("Thank you");
  const [rules, setRules] = useState([""]);
  const [noOfQuestions, setNoOfQuestions] = useState("10");
  const [soundToggle, setSoundToggle] = useState(true);
  const [thankyouToggle, setThankyouToggle] = useState(false);
  const [thankyouMessage, setThankyouMessage] = useState(defaultThankyouMessage);
  const [colors, setColors] = useState(defaultColors);

  useEffect(() => {
    dispatch(processQuestions());
    dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    setCustomTextThankYouPage(data.custom_text_thank_you_page || "Thank you");
    setRules(Array.isArray(data.rules) && data.rules.length ? data.rules : [""]);
    setNoOfQuestions(String(data.no_of_questions ?? "10"));
    setSoundToggle(data.sound_toogle === "true" || data.sound_toogle === true);
    setThankyouToggle(data.thankyou_toggle === "true" || data.thankyou_toggle === true);
    setThankyouMessage(
      clampThankyouOutcomeMessage(
        migrateThankyouMessage(data.thankyou_message),
        THANK_YOU_OUTCOME_MAX_CHARS
      )
    );
    setColors((prev) => ({
      ...prev,
      energy_color: data.energy_color ?? prev.energy_color,
      time_color: data.time_color ?? prev.time_color,
      heart_color: data.heart_color ?? prev.heart_color,
      heading_text_color: data.heading_text_color ?? prev.heading_text_color,
      question_color: data.question_color ?? prev.question_color,
      button_text_color: data.button_text_color ?? prev.button_text_color,
      option_bg_color: data.option_bg_color ?? prev.option_bg_color,
      main_color_box: data.main_color_box ?? prev.main_color_box,
      do_u_know_bg_color: data.do_u_know_bg_color ?? prev.do_u_know_bg_color,
      do_u_know_bg_text_color: data.do_u_know_bg_text_color ?? prev.do_u_know_bg_text_color,
      box_text_color: data.box_text_color ?? prev.box_text_color,
      box_bg_color: data.box_bg_color ?? prev.box_bg_color,
    }));
  }, [data]);

  const handleColorChange = (key) => (e) => {
    setColors((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleRuleChange = (idx, val) => {
    if (val.length > 200) {
      Swal.fire("Limit Exceeded", "Rules can be at most 200 characters.", "warning");
      val = val.slice(0, 200);
    }
    const arr = [...rules];
    arr[idx] = val;
    setRules(arr);
  };

  const handleAddRule = () => {
    if (rules.length >= 6) {
      return Swal.fire("Limit Reached", "You can only add up to 6 rules.", "warning");
    }
    setRules([...rules, ""]);
  };

  const handleRemoveRule = (idx) => {
    if (rules.length <= 1) {
      return Swal.fire("At Least One Rule Required", "You must have at least one rule.", "warning");
    }
    setRules(rules.filter((_, i) => i !== idx));
  };

  const handleThankyouRangeChange = (category, rangeKey, val) => {
    if (val.length > THANK_YOU_OUTCOME_MAX_CHARS) {
      void Swal.fire({
        icon: "warning",
        title: "Max limit crossed",
        text: `Thank you outcome messages can be at most ${THANK_YOU_OUTCOME_MAX_CHARS} characters each.`,
      });
      val = val.slice(0, THANK_YOU_OUTCOME_MAX_CHARS);
    }
    setThankyouMessage((prev) => ({
      ...prev,
      [category]: {
        ...(typeof prev[category] === "object" && !Array.isArray(prev[category]) ? prev[category] : {}),
        [rangeKey]: val,
      },
    }));
  };

  const handleSubmit = async () => {
    if (!adminToken) {
      Swal.fire("Error", "Session expired. Please log in again.", "error");
      return;
    }

    const filledRules = rules.map((r) => r.trim()).filter(Boolean);
    if (filledRules.length === 0) {
      Swal.fire({
        title: "Validation",
        text: "Add at least one game rule. Rules cannot be left blank.",
        icon: "error",
      });
      return;
    }

    const payload = {
      data: {
        landing_title: data?.landing_title ?? "",
        themeDescription:
          data?.themeDescription != null ? String(data.themeDescription) : "",
        custom_text_thank_you_page: customTextThankYouPage,
        rules: filledRules,
        no_of_questions: String(noOfQuestions),
        sound_toogle: soundToggle ? "true" : "false",
        thankyou_toggle: thankyouToggle ? "true" : "false",
        thankyou_message: thankyouMessage,
        ...colors,
      },
      organizationId: organizationId || "admin",
      sessionId: sessionId || "admin",
      currentTheme,
    };

    try {
      await updateThemeData({ payload, token: adminToken });
      Swal.fire("Saved", "Theme settings saved successfully.", "success");
      navigate("/admin/addcontent");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save. Please try again.", "error");
    }
  };

  return (
    <>
      <div className="back-button-holder">
        <button className="back-button" onClick={() => navigate("/admin/")}>
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>
      <div className="rules-page-new">
        <div className="admin-tabs-holder">
          <div className="rules-page-columns">
            <div className="rules-page-left">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-pen"></i> &nbsp; Theme details
                </div>
                <div className="form-group-v1">
                  <label>Thank you text</label>
                  <input
                    type="text"
                    value={customTextThankYouPage}
                    onChange={(e) => setCustomTextThankYouPage(e.target.value.slice(0, 200))}
                    placeholder="Thank you"
                    className="form-control"
                    maxLength={200}
                  />
                  <div className="code">Max 200 characters.</div>
                </div>
                <div className="form-group-v1">
                  <label>No. of questions</label>
                  <input
                    type="number"
                    min={1}
                    max={99}
                    value={noOfQuestions}
                    onChange={(e) => setNoOfQuestions(e.target.value)}
                    className="form-control"
                  />
                </div>
                <div className="form-group-v1 toogle-row">
                  <label>Sound</label>
                  <Switch checked={soundToggle} onChange={setSoundToggle} />
                </div>
                <div className="form-group-v1 toogle-row">
                  <label>Thank you message (custom message per outcome)</label>
                  <Switch checked={thankyouToggle} onChange={setThankyouToggle} />
                </div>
                {thankyouToggle && (
                  <>
                    <div className="code" style={{ marginBottom: 8 }}>
                      Scores 0–29, 30–59, 60–100 (one message per line in each box).{" "}
                      <strong>Max {THANK_YOU_OUTCOME_MAX_CHARS} characters</strong> per field.
                    </div>
                    {["energy", "time", "heart"].map((cat) => (
                      <div key={cat} className="form-group-v1">
                        <label style={{ textTransform: "capitalize" }}>
                          {cat} — 0–29 (max {THANK_YOU_OUTCOME_MAX_CHARS} characters)
                        </label>
                        <textarea
                          rows={3}
                          value={thankyouMessage[cat]?.range_0_30 || ""}
                          onChange={(e) => handleThankyouRangeChange(cat, "range_0_30", e.target.value)}
                          className="form-control rules-textarea"
                          placeholder="One or more lines"
                          maxLength={THANK_YOU_OUTCOME_MAX_CHARS}
                        />
                      </div>
                    ))}
                    {["energy", "time", "heart"].map((cat) => (
                      <div key={`${cat}-mid`} className="form-group-v1">
                        <label style={{ textTransform: "capitalize" }}>
                          {cat} — 30–59 (max {THANK_YOU_OUTCOME_MAX_CHARS} characters)
                        </label>
                        <textarea
                          rows={3}
                          value={thankyouMessage[cat]?.range_30_60 || ""}
                          onChange={(e) => handleThankyouRangeChange(cat, "range_30_60", e.target.value)}
                          className="form-control rules-textarea"
                          placeholder="One or more lines"
                          maxLength={THANK_YOU_OUTCOME_MAX_CHARS}
                        />
                      </div>
                    ))}
                    {["energy", "time", "heart"].map((cat) => (
                      <div key={`${cat}-hi`} className="form-group-v1">
                        <label style={{ textTransform: "capitalize" }}>
                          {cat} — 60–100 (max {THANK_YOU_OUTCOME_MAX_CHARS} characters)
                        </label>
                        <textarea
                          rows={3}
                          value={thankyouMessage[cat]?.range_60_plus || ""}
                          onChange={(e) => handleThankyouRangeChange(cat, "range_60_plus", e.target.value)}
                          className="form-control rules-textarea"
                          placeholder="One or more lines"
                          maxLength={THANK_YOU_OUTCOME_MAX_CHARS}
                        />
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>

            <div className="rules-page-center">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-trophy"></i> &nbsp; Game Rules
                </div>
                <h3 className="custom-rules-title">Custom rules</h3>
                {rules.map((rule, i) => (
                  <div className="rule-row" key={i}>
                    <span className="rule-number">{i + 1}.</span>
                    <textarea
                      className="rule-input rule-textarea"
                      placeholder="Enter rule"
                      value={rule}
                      onChange={(e) => handleRuleChange(i, e.target.value)}
                      rows={3}
                    />
                    <button className="remove-btn" onClick={() => handleRemoveRule(i)}>
                      &times;
                    </button>
                  </div>
                ))}
                <div className="rules-button-container">
                  <button className="add-btn" onClick={handleAddRule}>
                    + Add New Rule
                  </button>
                  <div className="code">
                    Max 6 rules. At least 1 required. 200 characters per rule.
                  </div>
                </div>
              </div>
            </div>

            <div className="rules-page-right">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-palette"></i> &nbsp; Colors
                </div>
                <div className="pallet-holder">
                  {[
                    { key: "energy_color", label: "Energy" },
                    { key: "time_color", label: "Time" },
                    { key: "heart_color", label: "Heart" },
                    { key: "heading_text_color", label: "Rules/heading text " },
                    { key: "question_color", label: "Question" },
                    { key: "button_text_color", label: "Option answer color" },
                    { key: "option_bg_color", label: "Option bg color" },
                    { key: "main_color_box", label: "QnA color box " },
                    { key: "do_u_know_bg_color", label: "Do you know BG" },
                    { key: "do_u_know_bg_text_color", label: "Do you know text" },
                    { key: "box_bg_color", label: "Button BG" },
                    { key: "box_text_color", label: "Button text" },
                  ].map(({ key, label }) => (
                    <div className="pallet-row pallet-row--label-left" key={key}>
                      <label>{label}</label>
                      <input
                        type="color"
                        className="pallet-swatch"
                        value={colors[key] || "#000000"}
                        onChange={handleColorChange(key)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="rules-action-holder">
            <button className="save-and-continue" onClick={handleSubmit}>
              Save
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Rules;
